import React from 'react';
import classNames from 'classnames';
import { action } from 'mobx';
import { CommonTestAttributes } from 'chaika-test-attributes';

import style from './TaskRightMenu.module.scss';

import { store } from '@store';
import { MeatballButtonMenuItem } from '@components/ActionButtons/MeatballButton';

import { TaskFormProvider, useTaskForm } from './context/TaskFormContext';
import { TaskPreloader } from './components/TaskPreloader';
import { TaskParticipants } from './components/TaskParticipants';
import { TaskRightMenuTitle } from './components/TaskRightMenuTitle';
import { TaskDateAndUrgency } from './components/TaskDateAndUrgency';
import { TaskDescription } from './components/TaskDescription';
import { TaskPatientData } from './components/TaskPatientData';
import { TaskActions } from './components/TaskActions';
import { TaskExecutorComment } from './components/TaskExecutorComment';

interface Props {
    taskId?: string;
    patientId?: string;
    medicalCaseIds?: string[];
    entryId?: string;
    isPatientOptional?: boolean;
}

export function TaskRightMenu({ isPatientOptional, ...formProps }: Props): React.ReactElement<any> {
    return (
        <TaskFormProvider {...formProps}>
            <TaskRightMenuContent isPatientOptional={isPatientOptional} />
        </TaskFormProvider>
    );
}

function TaskRightMenuContent({ isPatientOptional }: Pick<Props, 'isPatientOptional'>): React.ReactElement<any> {
    const { state, isLoading } = useTaskForm();

    return isLoading ? (
        <TaskPreloader />
    ) : (
        <>
            <TaskRightMenuTitle />
            <div className={style.fields}>
                <TaskPatientData isPatientOptional={isPatientOptional} />
                <TaskDescription isTypeShownInHeader />
                <div className={classNames([style.group, !state.isEditMode && style.group_view])}>
                    <TaskDateAndUrgency />
                    <TaskParticipants />
                </div>
                <TaskExecutorComment />
                <TaskActions />
            </div>
        </>
    );
}

const closeRightMenu = action(() => store.app.resetRightMenu());

const openRightMenu = action(() => {
    store.app.setRightMenu({
        isShowing: true,
        stickyButtonsLayout: true,
        children: React.createElement(TaskRightMenu, { isPatientOptional: true }),
        onClickOutside: closeRightMenu,
    });
});

export const TaskRightMenuAction: MeatballButtonMenuItem = {
    label: 'createTask',
    onClick: openRightMenu,
    dataTest: CommonTestAttributes.ActionsCreateTask,
};
